import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
    RocketLaunchIcon,
    EyeIcon,
    TrophyIcon,
    ShieldCheckIcon,
    UserGroupIcon,
    AcademicCapIcon,
    CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { useFirestore } from '../hooks/useFirestore';
import { getAboutInfo } from '../firebase/firestore';
import Section from '../components/common/Section';
import Loading from '../components/common/Loading';
import Card from '../components/common/Card';
import hero1 from '../assets/images/hero-1.png';
import './About.css';

const About = () => {
    const { t, i18n } = useTranslation();
    const { data: aboutInfo, loading } = useFirestore(useCallback(() => getAboutInfo(), []));

    const whyChooseItems = [
        { key: 'quality', icon: TrophyIcon },
        { key: 'experience', icon: AcademicCapIcon },
        { key: 'service', icon: UserGroupIcon },
        { key: 'innovation', icon: RocketLaunchIcon }
    ];

    const isAr = i18n.language === 'ar';

    return (
        <div className="about-page">
            <Helmet>
                <title>{t('about.title')} | {t('hero.title')}</title>
                <meta name="description" content={t('about.subtitle')} />
                <html lang={i18n.language} dir={isAr ? 'rtl' : 'ltr'} />
            </Helmet>

            <main>
                {/* Page Header */}
                <div className="page-header">
                    <div className="container">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-center"
                        >
                            <h1>{t('about.title')}</h1>
                            <div className="accent-line center"></div>
                            <p className="header-subtitle">{t('about.subtitle')}</p>
                        </motion.div>
                    </div>
                </div>

                {loading ? (
                    <Section>
                        <Loading message={t('common.loading')} />
                    </Section>
                ) : (
                    <>
                        {/* Company Story */}
                        <Section className="about-story">
                            <div className="grid grid-2 align-center">
                                <motion.div
                                    initial={{ opacity: 0, x: isAr ? 50 : -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <div className="section-label">{t('about.whoWeAre')}</div>
                                    <h2>{aboutInfo?.titleAr || t('about.title')}</h2>
                                    <div className="accent-line"></div>
                                    <p className="lead-text">
                                        {aboutInfo
                                            ? (isAr ? aboutInfo.descAr : aboutInfo.descEn)
                                            : t('about.subtitle')
                                        }
                                    </p>
                                </motion.div>

                                <motion.div
                                    className="about-image-card shadow-premium"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <img src={hero1} alt="Sigma Office" className="rounded-24" />
                                </motion.div>
                            </div>
                        </Section>

                        {/* Mission & Vision */}
                        {aboutInfo && (aboutInfo.mission || aboutInfo.vision) && (
                            <Section className="mission-vision bg-white-soft">
                                <div className="grid grid-2">
                                    {aboutInfo.mission && (
                                        <Card hover={true} className="mv-card">
                                            <div className="card-icon">
                                                <RocketLaunchIcon className="icon-inner" />
                                            </div>
                                            <h3>{t('about.mission')}</h3>
                                            <p>{isAr ? aboutInfo.mission.ar : aboutInfo.mission.en}</p>
                                        </Card>
                                    )}

                                    {aboutInfo.vision && (
                                        <Card hover={true} className="mv-card">
                                            <div className="card-icon">
                                                <EyeIcon className="icon-inner" />
                                            </div>
                                            <h3>{t('about.vision')}</h3>
                                            <p>{isAr ? aboutInfo.vision.ar : aboutInfo.vision.en}</p>
                                        </Card>
                                    )}
                                </div>
                            </Section>
                        )}

                        {/* Why Choose Sigma */}
                        <Section className="why-choose">
                            <div className="section-title text-center">
                                <h2>{t('about.whyChoose')}</h2>
                                <div className="accent-line center"></div>
                            </div>

                            <div className="grid grid-4 text-center">
                                {whyChooseItems.map((item, index) => (
                                    <motion.div
                                        key={item.key}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="why-item"
                                    >
                                        <div className="why-icon-box">
                                            <item.icon className="icon-inner" />
                                        </div>
                                        <h4>{t(`about.reasons.${item.key}.title`)}</h4>
                                        <p>{t(`about.reasons.${item.key}.desc`)}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </Section>
                    </>
                )}
            </main>
        </div>
    );
};

export default About;
